import { defineStore } from "pinia";
import { getTeamInfo } from "@/api/getInfo";
import {
  createPersistenceConfig,
  readCache,
  writeCache,
  clearCache,
  isCacheAvailable,
  isCacheExpiring,
  getCacheInfo,
  type CacheInfo,
} from "@/utils/cache";

// ========== 持久化配置 ==========
const PERSISTENCE_CONFIG = createPersistenceConfig("teamList");

export type teamType = {
  advantages: { advantage: string; sortOrder: number }[];
  description: string;
  id: number;
  name: string;
  photographs: { src: string; alt: string; sortOrder: number }[];
};

interface TeamsState {
  teamList: teamType[];
}

export const useTeamsStore = defineStore("teams", {
  state: (): TeamsState => ({
    teamList: [],
  }),
  getters: {
    getTeamsList: (state) => state.teamList,
  },
  actions: {
    async fetchTeamsList(
      forceRefresh = false,
      silentRefresh = false,
    ): Promise<void> {
      // 1. 检查内存缓存
      if (this.teamList.length > 0 && !forceRefresh) {
        return;
      }

      // 2. 检查 localStorage 缓存
      if (!forceRefresh) {
        const cachedData = readCache<teamType[]>(PERSISTENCE_CONFIG);

        if (cachedData && Array.isArray(cachedData)) {
          this.teamList = cachedData;

          // 静默刷新：缓存即将过期时后台异步刷新
          if (silentRefresh && isCacheExpiring(PERSISTENCE_CONFIG)) {
            this.refreshInBackground();
          }

          return;
        }
      }

      // 3. 请求接口
      try {
        const response = await getTeamInfo();
        const data = response?.data;

        if (!this.isValidTeamArray(data)) {
          throw new Error("接口返回数据格式异常");
        }

        this.teamList = data;
        writeCache(PERSISTENCE_CONFIG, data);
      } catch (error) {
        // 降级策略
        if (isCacheAvailable(PERSISTENCE_CONFIG)) {
          const fallbackData = readCache<teamType[]>(PERSISTENCE_CONFIG);
          if (fallbackData && Array.isArray(fallbackData)) {
            this.teamList = fallbackData;
            return;
          }
        }

        if (this.teamList.length === 0) {
          throw error;
        }
      }
    },

    async refreshInBackground(): Promise<void> {
      try {
        const response = await getTeamInfo();
        const data = response?.data;

        if (this.isValidTeamArray(data)) {
          this.teamList = data;
          writeCache(PERSISTENCE_CONFIG, data);
        }
      } catch (error) {}
    },

    async refreshTeams(): Promise<void> {
      return this.fetchTeamsList(true, false);
    },

    clearCache(): void {
      this.teamList = [];
      clearCache(PERSISTENCE_CONFIG);
    },

    getCacheInfo(): CacheInfo {
      return getCacheInfo(PERSISTENCE_CONFIG);
    },

    isValidTeamArray(data: unknown): data is teamType[] {
      return (
        Array.isArray(data) &&
        data.every(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            "id" in item &&
            typeof (item as teamType).id === "number",
        )
      );
    },

    updateTeam(updatedTeam: teamType): void {
      const index = this.teamList.findIndex((t) => t.id === updatedTeam.id);
      if (index !== -1) {
        this.teamList[index] = { ...this.teamList[index], ...updatedTeam };
        writeCache(PERSISTENCE_CONFIG, this.teamList);
      }
    },
  },
});
